import { Link } from "react-router-dom";
import { ArrowRight, Wand2, Gamepad2, Globe, NotebookPen, Hammer, Dna, FlaskConical } from "lucide-react";
import Reveal from "../components/Reveal";

/**
 * Lab — every DPA app framed as a laboratory for a concept learned in
 * the curriculum.  Preserves the existing app routes; this page is a new
 * on-ramp, not a replacement.
 */
const LABS = [
  {
    slug: "prompt-studio",
    to: "/studio",
    name: "Prompt Studio",
    icon: Wand2,
    tint: "bg-saffron/15 text-marigold",
    concept: "State · Data models · UI structure",
    body: "Describe an Apple app in plain English. Watch a real SwiftUI project appear. The lab for turning intent into working screens.",
    from: "Programming · Software Engineering · HCI",
  },
  {
    slug: "game-studio",
    to: "/games",
    name: "Game Studio",
    icon: Gamepad2,
    tint: "bg-teal/15 text-teal",
    concept: "Loops · State machines · Input · Timing",
    body: "SpriteKit and SceneKit as a laboratory. Every game is a state machine you can watch tick.",
    from: "Programming · Architecture · HCI",
  },
  {
    slug: "website-studio",
    to: "/website-studio",
    name: "Website Studio",
    icon: Globe,
    tint: "bg-marigold/15 text-marigold",
    concept: "Networks · HTTP · Deployment",
    body: "Describe a website. Get a Next.js project. Open the deploy panel and watch DNS + HTTP become concrete.",
    from: "Internet & Web · Software Engineering",
  },
  {
    slug: "knuth",
    to: "/knuth",
    name: "Knuth",
    icon: NotebookPen,
    tint: "bg-ink/[0.08] text-ink",
    concept: "Software architecture · Pseudocode · UML",
    body: "Point Knuth at a repo. It reads the code the way you'd read a novel, and hands back a documentation, diagrams and traceable pseudocode.",
    from: "Software Engineering · Programming",
  },
  {
    slug: "kailash",
    to: "/kailash",
    name: "Kailash",
    icon: Hammer,
    tint: "bg-marigold/12 text-marigold",
    concept: "Compilers · Packaging · Releases",
    body: "The hardware/software boundary made touchable. Import a project, pick a target, get a real .deb, .rpm or PKGBUILD.",
    from: "Architecture · Software Engineering",
  },
  {
    slug: "mendel",
    to: "/apps/mendel",
    name: "Mendel",
    icon: Dna,
    tint: "bg-teal/12 text-teal",
    concept: "Sequences · State · Simulation",
    body: "Programming Biology. Read a gene. Simulate an inheritance pattern. The workbench for when the program is written in four letters.",
    from: "Programming Biology · Computational Science",
  },
];

export default function Lab() {
  return (
    <div className="container-narrow py-14 md:py-20" data-testid="lab-page">
      <Reveal className="mb-12">
        <div className="inline-flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.28em] font-bold text-marigold">
          <FlaskConical className="w-3.5 h-3.5" /> The Lab
        </div>
        <h1 className="font-heading text-5xl md:text-6xl font-semibold text-ink leading-[1.05] mb-4 max-w-3xl">
          Where the reading gets its <span className="italic text-marigold gold-underline">hands dirty</span>.
        </h1>
        <p className="text-lg text-ink2 leading-relaxed max-w-2xl">
          Every DPA app is a laboratory for a concept you just read about. Open one,
          break it, put it back together. When you're stuck, Ms. Pradhan is one tap away.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-5" data-testid="lab-grid">
        {LABS.map((lab, i) => (
          <Reveal key={lab.slug} delay={(i % 4) + 1}>
            <LabCard {...lab} />
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-14">
        <div className="rounded-3xl border-2 border-ink bg-cream shadow-[8px_8px_0_0_#1F1A17] p-8">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-2">The loop</div>
          <h3 className="font-heading text-2xl font-semibold text-ink leading-tight mb-3">
            Read → understand → experiment → build → create.
          </h3>
          <p className="text-sm text-ink2 leading-relaxed max-w-2xl">
            The curriculum teaches you the ideas. The lab lets you feel them.
            The road trip gives you a story to hang them on. Nothing here is a demo —
            every export builds, every project ships.
          </p>
        </div>
      </Reveal>
    </div>
  );
}

function LabCard({ to, name, icon: Icon, tint, concept, body, from }) {
  return (
    <Link
      to={to}
      className="block rounded-3xl border-2 border-ink bg-cream p-6 md:p-7 shadow-[6px_6px_0_0_#1F1A17] hover:-translate-y-1 hover:shadow-[10px_10px_0_0_#1F1A17] transition-all group"
      data-testid={`lab-card-${name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${tint}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">Laboratory</div>
      <h3 className="font-heading text-2xl font-semibold text-ink leading-tight mb-2 group-hover:text-marigold transition-colors">
        {name}
      </h3>
      <p className="text-sm text-ink2 leading-relaxed mb-4">{body}</p>
      <div className="text-[11px] font-mono text-ink2 mb-1">
        <span className="text-marigold font-bold">experiments</span> · {concept}
      </div>
      <div className="text-[11px] font-mono text-ink2 mb-5">
        <span className="text-marigold font-bold">reading</span> · {from}
      </div>
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink group-hover:text-marigold transition-colors">
        Enter the lab <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}
